import styled, { css } from 'styled-components';
import { FlexJustifySpaceBetweenAlignCenter } from 'components/primitives/flex';
import { RefObject } from 'react';

interface CounterIconProps {
  type?: 'plus' | 'minus';
}

const CounterIcon = ({ type = 'plus' }: CounterIconProps) => {
  const Icon = type === 'plus' ? CounterPlusIcon : CounterMinusIcon;

  return (
    <IconWrapper>
      <Icon />
    </IconWrapper>
  );
};

const CounterMinusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="2" viewBox="0 0 18 2" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0 1C0 0.447715 0.447715 0 1 0H17C17.5523 0 18 0.447715 18 1C18 1.55228 17.5523 2 17 2H1C0.447715 2 0 1.55228 0 1Z"
      fill="#17233C"
    />
  </svg>
);

const CounterPlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10 0.947368C10 0.424151 9.55228 0 9 0C8.44771 0 8 0.424151 8 0.947368V8H1C0.447715 8 0 8.44771 0 9C0 9.55228 0.447715 10 1 10H8V17.0526C8 17.5758 8.44771 18 9 18C9.55228 18 10 17.5758 10 17.0526V10H17C17.5523 10 18 9.55228 18 9C18 8.44771 17.5523 8 17 8H10V0.947368Z"
      fill="#17233C"
    />
  </svg>
);

interface CounterProps {
  minimum: number;
  boatCapacity: number;
  value: number;
  buttonSize?: number;
  onClick?: (count: number) => void;
  register?: RefObject<HTMLInputElement>;
  disabled?: boolean;
}

const Counter = ({ minimum, disabled, boatCapacity, value, register, onClick, buttonSize = 50 }: CounterProps) => {
  const downDisabled = disabled || value <= minimum;
  const upDisabled = disabled || value >= boatCapacity;

  return (
    <CounterWrapper>
      <div style={{ width: buttonSize, height: buttonSize }}>
        <CounterButton
          data-testid="minus-button"
          isDisabled={downDisabled}
          onClick={() => (downDisabled ? null : onClick(value - 1))}
        >
          <CounterIcon type="minus" />
        </CounterButton>
      </div>
      <CounterLabel>
        <input min={1} name="passengers" type="hidden" value={value} ref={register || null} readOnly />
        <CounterSpan data-testid="passengers">{value === 14 ? `${value}+` : value}</CounterSpan>
      </CounterLabel>
      <div style={{ width: buttonSize, height: buttonSize }}>
        <CounterButton
          data-testid="plus-button"
          isDisabled={upDisabled}
          onClick={() => (upDisabled ? null : onClick(value + 1))}
        >
          <CounterIcon type="plus" />
        </CounterButton>
      </div>
    </CounterWrapper>
  );
};

export default Counter;

const CounterWrapper = styled(FlexJustifySpaceBetweenAlignCenter)`
  width: 100%;
  height: 100%;
`;

const CounterButton = styled.button.attrs({ type: 'button' })<{ isDisabled: boolean }>`
  border-radius: 8px;
  &:focus {
    outline: 0;
  }
  ${({ isDisabled }) =>
    isDisabled &&
    css`
      filter: grayscale(100%);
      opacity: 0.4;
    `};
`;

const CounterLabel = styled.div`
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #17233c;
  text-align: center;
  border: none;
`;

const CounterSpan = styled.span`
  font-size: 18px;
  font-weight: 500;
`;

const IconWrapper = styled.div`
  width: 50px;
  display: flex;
  padding: 15px 16px;
  justify-content: center;
  align-items: center;
  height: 50px;
  border-radius: 8px;
  border: 1px solid #dbdfe5;
  background: #fff;
`;
